import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { trackEvent } from '../services/analytics';
import { getAdminToken, getAdminUser } from '../services/adminAuth';

interface AuthState {
  isAuthenticated: boolean;
  user?: {
    tier: string;
    isAdmin?: boolean;
    // ... other user properties
  };
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const location = useLocation();
  const { isAuthenticated, hasCompletedOnboarding, needsOnboarding, user } = useSelector((state: RootState) => state.auth);
  const userTier = user?.tier || 'free';
  const isAdmin = user?.isAdmin || false;
  
  // Check for admin token authentication
  const adminToken = getAdminToken();
  const adminUser = getAdminUser();
  const hasAdminAuth = requireAdmin && adminToken && adminUser?.is_admin;

  // AuthFlow handles authentication and onboarding redirects
  // ProtectedRoute only handles specific authorization checks
  
  if (!isAuthenticated && !hasAdminAuth) {
    // Track unauthorized access attempt
    trackEvent('unauthorized_access', {
      path: location.pathname,
      reason: 'not_authenticated'
    });
    
    // For admin routes, redirect to admin login
    if (requireAdmin) {
      return <Navigate to="/orbit-control-9e2f1a/login" state={{ from: location }} replace />;
    }
    
    // For regular routes, redirect to auth page
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check if route requires paid subscription (except for pro users)
  const requiresPaidSubscription = ['/simulation', '/photos'].includes(location.pathname);
  if (requiresPaidSubscription && userTier !== 'pro' && !requireAdmin && !hasAdminAuth) {
    // Track unauthorized access attempt
    trackEvent('unauthorized_access', {
      path: location.pathname,
      reason: 'subscription_required'
    });
    
    // Redirect to pricing page
    return <Navigate to="/pricing" state={{ from: location }} replace />;
  }

  // Check if route requires admin access
  if (requireAdmin && !isAdmin && !hasAdminAuth) {
    // Track unauthorized access attempt
    trackEvent('unauthorized_access', {
      path: location.pathname,
      reason: 'admin_required'
    });
    
    // Redirect to admin login
    return <Navigate to="/orbit-control-9e2f1a/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;